// Libraries
import { action, computed, observable } from 'mobx';
import { app } from '../App';

// Interfaces
import { IUser, UserType, IUserCreationInfo } from 'common/interfaces/user';

interface IAuthenticationUser extends IAuthentication {
  user: IUser;
}
interface IAuthentication {
  accessToken: string;
}

export interface IEditedUser {
  name: string;
  email: string;
  modified: boolean;
}

export interface IEditedUserPassword {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
  modified: boolean;
}

interface ITokenPayload {
  aud: string;
  exp: number;
  iat: number;
  iss: string;
  sub: string;
  userId: number;
}

interface IAuthenticationResponse {
  accessToken: string;
  user: {
    id: number;
    username: string;
    email: string | null;
    name: string;
    slpcode: string | null;
    type: string;
    status: 1 | 0;
  };
}

// Enumerables
export enum UserEditableProperties {
  name = 'name',
  email = 'email',
  currentPassword = 'currentPassword',
  newPassword = 'newPassword',
  confirmPassword = 'confirmPassword',
}

// Store
export class User {
  // User info
  @observable public id: number | null = null;
  @observable public username: string | null = null;
  @observable public name: string | null = null;
  @observable public email: string | null = null;
  @observable public maxFinishedActivitiesCount: number | null = null;
  @observable public userType: UserType | null = null;
  @observable public slpcode: number | null = null;

  // Edit user
  @observable public userEdit: boolean = false;
  @observable public userEditSaving: boolean = false;
  @observable public userEditSavingError: boolean = false;
  @observable public editedUser: IEditedUser = {
    name: '',
    email: '',
    modified: false,
  };

  // Edit user password
  @observable public userChangePassword: boolean = false;
  @observable public userChangePasswordSaving: boolean = false;
  @observable public userChangePasswordSavingError: boolean = false;
  @observable public userChangePasswordSavingSuccess: boolean = false;
  @observable public changeUserPassword: IEditedUserPassword = {
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
    modified: false,
  };

  // Start editing user info
  @action public startEditingUser = () => {
    this.userEdit = true;
    this.userEditSaving = false;
    this.userEditSavingError = false;
    this.editedUser = {
      name: this.name || '',
      email: this.email || '',
      modified: false,
    };
  };

  // Stop editing user info
  @action public stopEditingUser = () => {
    this.userEdit = false;
    this.userEditSaving = false;
    this.userEditSavingError = false;
    this.editedUser = {
      name: '',
      email: '',
      modified: false,
    };
  };

  // Start change user password
  @action public startEditingUserPassword = () => {
    this.resetEditingUserPassword(true);
  };

  // Stop change user password
  @action public stopEditingUserPassword = () => {
    this.resetEditingUserPassword(false);
  };

  @action public resetEditingUserPassword = (openPasswordEditForm: boolean) => {
    this.userChangePassword = openPasswordEditForm;
    this.userChangePasswordSaving = false;
    this.userChangePasswordSavingError = false;
    this.userChangePasswordSavingSuccess = false;
    this.changeUserPassword = {
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
      modified: false,
    };
  };

  @action public updateUserPassword = async (): Promise<any> => {
    try {
      this.userChangePasswordSaving = true;
      this.userChangePasswordSavingError = false;
      this.userChangePasswordSavingSuccess = false;

      if (this.changeUserPassword.modified) {
        if (
          this.changeUserPassword.currentPassword &&
          this.changeUserPassword.newPassword &&
          this.changeUserPassword.confirmPassword
        ) {
          const service = app.service('users');
          const patch: IUser = await service.patch(this.id, {
            currentPassword: this.changeUserPassword.currentPassword,
            newPassword: this.changeUserPassword.newPassword,
            confirmPassword: this.changeUserPassword.confirmPassword,
          });

          this.userChangePasswordSavingSuccess = true;

          this.changeUserPassword = {
            currentPassword: '',
            newPassword: '',
            confirmPassword: '',
            modified: false,
          };
        } else {
          throw new Error('All password fields are required');
        }
      } else {
        this.resetEditingUserPassword(false);
      }

      return true;
    } catch (err) {
      this.userChangePasswordSavingError = true;
      console.error(err);
      return err;
    } finally {
      this.userChangePasswordSaving = false;
    }
  };

  @action public saveUpdatedUser = async (): Promise<any> => {
    try {
      this.userEdit = false;
      this.userEditSaving = true;
      this.userEditSavingError = false;

      if (this.editedUser.modified) {
        const service = app.service('users');
        const patch: IUser = await service.patch(this.id, {
          name: this.editedUser.name.trim(),
          email: this.editedUser.email.trim(),
        });

        this.name = patch.name;
        this.email = patch.email;
      }

      this.userEdit = false;
      this.userEditSaving = false;
      this.userEditSavingError = false;

      const result = this.editedUser;

      this.editedUser = {
        name: '',
        email: '',
        modified: false,
      };

      return result;
    } catch (err) {
      this.userEditSaving = false;
      this.userEditSavingError = true;
      console.error(err);
      return err;
    }
  };

  // @observable public userType: string | null = null;
  @computed public get isLoggedIn(): boolean {
    return this.id && this.username ? true : false;
  }

  // Change max activities preference
  @action public changeMaxActivitiesPreference = async (
    maxActivities: number
  ): Promise<number> => {
    try {
      this.maxFinishedActivitiesCount = maxActivities;

      const userService = app.service('users');
      const user: IUser = await userService.patch(this.id, {
        maxActivity: maxActivities,
      });

      return maxActivities;
    } catch (err) {
      console.error(err);
      return err;
    }
  };

  // Relogin via local token
  @action public relogin = async (): Promise<any> => {
    await app.passport
      .getJWT()
      .then((token: string) => {
        if (token) {
          return app.passport.authenticate({
            type: 'local',
            accessToken: token,
          });
        } else {
          const err = new Error('No valid token stored');
          throw err.stack;
        }
      })
      .then((payload: IAuthenticationResponse) => {
        if (
          typeof payload !== 'undefined' &&
          payload !== null &&
          typeof payload.user.id !== 'undefined' &&
          payload.user.id
        ) {
          const srv = app.service('users');
          return app.service('users').get(payload.user.id);
        } else {
          const err = new Error('Token is invalid');
          throw err.stack;
        }
      })
      .then((user: IUser) => {
        if (typeof user !== 'undefined') {
          app.set('user', user);
          this.id = user.id;
          this.username = user.username;
          this.name = user.name;
          this.email = user.email;
          this.userType = user.type;
          this.slpcode = user.slpcode;
          this.maxFinishedActivitiesCount = user.maxActivity || 10;
          return user;
        } else {
          const err = new Error('User does not exist');
          throw err.stack;
        }
      })
      .catch((error) => {
        console.error(error);
        return error;
      });
  };

  // Login
  @action public login = async (
    username: string = '',
    password: string = ''
  ): Promise<any> => {
    await app
      .authenticate({
        type: 'local',
        strategy: 'local',
        username: username.trim(),
        password: password.trim(),
      })
      .then((response: IAuthentication) => {
        if (
          typeof response.accessToken !== 'undefined' &&
          response.accessToken
        ) {
          return app.passport.verifyJWT(response.accessToken);
        } else {
          const err = new Error('No access token returned');
          throw err.stack;
        }
      })
      .then((payload: ITokenPayload | any) => {
        if (
          typeof payload !== 'undefined' &&
          payload !== null &&
          typeof payload.userId !== 'undefined' &&
          payload.userId
        ) {
          return app.service('users').get(payload.userId);
        } else {
          const err = new Error('Token is invalid');
          throw err.stack;
        }
      })
      .then((user: IUser) => {
        if (typeof user !== 'undefined') {
          app.set('user', user);
          this.id = user.id;
          this.username = user.username;
          this.name = user.name;
          this.email = user.email;
          this.userType = user.type;
          this.slpcode = user.slpcode;
          this.maxFinishedActivitiesCount = user.maxActivity || 10;
          return user;
        } else {
          const err = new Error('User does not exist');
          throw err.stack;
        }
      })
      .catch((error) => {
        console.error(error);
        throw error;
      });
  };

  // Logout
  @action public logout = async (): Promise<any> => {
    try {
      await app.logout();
      this.id = null;
      this.username = null;
      this.userType = null;
      return true;
    } catch (err) {
      console.error(err);
      return err;
    }
  };
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--0!./app/web/src/stores/User.ts